import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { OptionType } from '@/types';
import type { MenuItemWithOptions, MenuOption, CartItemInput, SelectedOption } from '@/types';
import { modalTranslations } from '@/i18n';
import type { ApiMenuOptionGroup } from '@/lib/api/types';
import { useMenuOptionState } from '@/hooks';
import { OptionGroupSection, MenuQuantityControl } from '@/components/menu';
import { useApp } from '@/contexts/AppContext';
import { formatCurrency } from '@/utils/currency';
import { normalizeCustomerDisplayName, resolveCustomerDisplayName } from '@/utils/displayName';
import { formatTimeSlots, getTimeSlotsLabel } from '@/utils/menuAvailability';
import { normalizeSelectedOptions } from '@/utils/selectedOptions';

// 버그 수정: 빈 객체 기본값을 상수로 정의하여 참조 안정성 확보
const EMPTY_SELECTED_OPTIONS: Record<string, string[]> = {};
const EMPTY_INPUT_VALUES: Record<string, string> = {};

interface MenuOptionModalProps {
  item: MenuItemWithOptions | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (item: CartItemInput, quantity: number, selectedOptions?: SelectedOption[]) => void;
  initialQuantity?: number;
  initialSelectedOptions?: Record<string, string[]>;
  /**
   * #497: 입력형 옵션 (number_input/text_input/textarea) 초기 값 — cart 편집 시 복원.
   * 부재 시 빈 객체로 초기화. cart 편집 silent 누락 차단.
   */
  initialInputValues?: Record<string, string>;
  /** API 메뉴 옵션 (있으면 사용, 없으면 샘플 데이터 사용) */
  apiOptions?: ApiMenuOptionGroup[];
  /**
   * #541: view 전용 모드 — 담기 버튼 + 수량 컨트롤 미렌더.
   * 메뉴 정보(옵션·가격)는 표시됨. mode='view' 메뉴판에서 사용.
   */
  viewOnly?: boolean;
}

/**
 * API ApiMenuOptionGroup을 로컬 MenuOption 형식으로 변환
 */
function transformApiToLocalOption(apiGroup: ApiMenuOptionGroup): MenuOption {
  let optionType: OptionType;
  let minSelection: number;
  let maxSelection: number;

  switch (apiGroup.input_type) {
    case 'required_single':
      optionType = OptionType.REQUIRED_SINGLE;
      minSelection = 1;
      maxSelection = 1;
      break;
    case 'single':
      optionType = OptionType.OPTIONAL_SINGLE;
      minSelection = 0;
      maxSelection = 1;
      break;
    case 'required_multiple':
      optionType = OptionType.MULTI_RANGE;
      minSelection = apiGroup.min_select ?? 1;
      maxSelection = apiGroup.max_select ?? (apiGroup.options?.length ?? 999);
      break;
    case 'multiple':
      if (apiGroup.max_select !== null) {
        optionType = OptionType.MULTI_RANGE;
        minSelection = apiGroup.min_select ?? 0;
        maxSelection = apiGroup.max_select;
      } else {
        optionType = OptionType.MULTI_UNLIMITED;
        minSelection = apiGroup.min_select ?? 0;
        maxSelection = 999;
      }
      break;
    case 'text_input':
      optionType = OptionType.TEXT_INPUT;
      minSelection = 0;
      maxSelection = 0;
      break;
    case 'number_input':
      optionType = OptionType.NUMBER_INPUT;
      minSelection = 0;
      maxSelection = 0;
      break;
    case 'textarea':
      optionType = OptionType.TEXTAREA;
      minSelection = 0;
      maxSelection = 0;
      break;
    default:
      optionType = OptionType.OPTIONAL_SINGLE;
      minSelection = 0;
      maxSelection = 1;
  }

  return {
    id: String(apiGroup.id),
    nameKey: `api_option_${apiGroup.id}`,
    price: 0,
    type: optionType,
    minSelection,
    maxSelection,
    options: (apiGroup.options ?? []).map(choice => ({
      id: String(choice.id),
      nameKey: `api_choice_${choice.id}`,
      additionalPrice: choice.additional_price,
      quantityLimit: choice.quantity_limit,
      _name: normalizeCustomerDisplayName(choice.name),
    })),
    _name: normalizeCustomerDisplayName(apiGroup.name),
  };
}

export function MenuOptionModal({
  item,
  isOpen,
  onClose,
  onAddToCart,
  initialQuantity = 1,
  initialSelectedOptions = EMPTY_SELECTED_OPTIONS,
  initialInputValues = EMPTY_INPUT_VALUES,
  apiOptions,
  viewOnly = false,
}: MenuOptionModalProps) {
  const { locale, currency } = useApp();
  const [totalQuantity, setTotalQuantity] = useState(1);

  // 버그 수정: 모달이 열릴 때 초기화 여부를 추적
  const hasInitializedRef = useRef(false);

  const t = modalTranslations[locale];

  // API 옵션이 있으면 변환, 없으면 빈 배열 (샘플 데이터 사용 안 함)
  // #492: 기존 selection 만 통과시키던 필터를 input type 도 통과시키도록 변경.
  // - selection (single/multiple/required_*): options 배열 필수
  // - input (number_input/text_input/textarea): options 배열 무관 (값 입력)
  const menuOptions = useMemo(() => {
    if (apiOptions && apiOptions.length > 0) {
      return apiOptions
        .filter(opt => {
          const isSelectionType = ['single', 'multiple', 'required_single', 'required_multiple'].includes(opt.input_type);
          const isInputType = ['number_input', 'text_input', 'textarea'].includes(opt.input_type);
          if (isSelectionType) {
            return opt.options && opt.options.length > 0;
          }
          // input type 은 options 배열 없이도 통과
          return isInputType;
        })
        .map(transformApiToLocalOption);
    }
    return [];
  }, [apiOptions]);

  // 옵션이 있는지 확인
  const hasOptions = menuOptions.length > 0;

  // 메뉴 옵션 상태 훅
  // #493 (A2): 입력형 옵션 fully orderable — inputValues + handleInputChange 활성.
  // PR #494 의 hasPendingInputTypeOptions guard 는 본 PR 에서 해제 (UI 구현 완료).
  const {
    selectedOptions,
    validationErrors,
    inputValues,
    handleOptionSelect,
    handleOptionDecrement,
    handleInputChange,
    initializeOptions,
    setValidationErrors
  } = useMenuOptionState({ menuOptions });

  // 통화 포맷팅 함수
  const formatPrice = useCallback((price: number): string => {
    return formatCurrency(price, currency);
  }, [currency]);

  // 총 가격 계산 함수 (메뉴 기본가 + 옵션 추가가) × 수량
  const calculateTotalPrice = useCallback((quantity: number): number => {
    if (!item) return 0;

    // 옵션 추가 가격만 계산
    let optionsPrice = 0;
    menuOptions.forEach(optionGroup => {
      const selections = selectedOptions[optionGroup.id] || [];
      selections.forEach(selectionId => {
        const option = optionGroup.options.find(opt => opt.id === selectionId);
        if (option) {
          optionsPrice += option.additionalPrice;
        }
      });
    });

    // 단가 계산 (메뉴 기본가 + 옵션가)
    const basePrice = item.price;
    const unitPrice = basePrice + optionsPrice;

    // 총 가격 (단가 * 수량)
    return unitPrice * quantity;
  }, [item, menuOptions, selectedOptions]);

  // 안정적 참조 비교를 위한 직렬화 키 (unstable 객체 참조 문제 방지)
  const initialOptionsKey = useMemo(
    () => JSON.stringify(initialSelectedOptions),
    [initialSelectedOptions]
  );
  // #497: 입력형 옵션 초기값 직렬화 키 (cart 편집 시 inputValues 복원)
  const initialInputValuesKey = useMemo(
    () => JSON.stringify(initialInputValues),
    [initialInputValues]
  );

  // item 변경 추적 (모달 열린 상태에서 item이 바뀌는 경우 대응)
  const prevItemIdRef = useRef<string | null>(null);

  // 버그 수정: 모달이 열릴 때만 초기화 (useRef로 추적)
  // isOpen false 시 즉시 ref 리셋, item 변경 시 재초기화
  useEffect(() => {
    if (!isOpen) {
      hasInitializedRef.current = false;
      prevItemIdRef.current = null;
      return;
    }

    if (!item) return;

    const itemChanged = prevItemIdRef.current !== null && prevItemIdRef.current !== item.id;
    if (hasInitializedRef.current && !itemChanged) return;

    hasInitializedRef.current = true;
    prevItemIdRef.current = item.id;
    setValidationErrors({});
    setTotalQuantity(initialQuantity || 1);
    // #497: cart 편집 시 inputValues 도 복원 (silent 누락 차단)
    initializeOptions(initialSelectedOptions, initialInputValues);
  }, [isOpen, item, initialQuantity, initialOptionsKey, initialInputValuesKey, initializeOptions, setValidationErrors, initialSelectedOptions, initialInputValues]);

  // 장바구니에 추가
  const handleAddToCart = useCallback(() => {
    if (!item) {
      return;
    }

    // 버튼 클릭 시 validateAndScroll()로 이미 검증했으므로 중복 검증 제거

    // 선택된 옵션들을 설명 텍스트로 변환
    const customizations: string[] = [];
    const normalizedSelectedOptions = normalizeSelectedOptions(selectedOptions);

    menuOptions.forEach(optionGroup => {
      // #493: 입력형 옵션 (number_input/text_input/textarea) — 사용자 입력 값 첨부.
      const isInputType =
        optionGroup.type === OptionType.NUMBER_INPUT
        || optionGroup.type === OptionType.TEXT_INPUT
        || optionGroup.type === OptionType.TEXTAREA;
      if (isInputType) {
        const value = (inputValues[optionGroup.id] || '').trim();
        if (value) {
          const groupName = resolveCustomerDisplayName(
            optionGroup._name,
            t.optionGroups[optionGroup.nameKey as keyof typeof t.optionGroups],
            optionGroup.nameKey,
          ) ?? optionGroup.id;
          customizations.push(`${groupName}: ${value}`);
        }
        return;
      }

      const selections = normalizedSelectedOptions[optionGroup.id] || [];
      selections.forEach(selectionId => {
        const option = optionGroup.options.find(opt => opt.id === selectionId);
        if (option) {
          // API 옵션은 _name 사용, 아니면 번역 시스템 사용
          const optionName = resolveCustomerDisplayName(
            option._name,
            t.options[option.nameKey as keyof typeof t.options],
            option.nameKey,
          ) ?? option.id;
          customizations.push(optionName);
        }
      });
    });

    const cartItem = {
      id: `${item.id}-${Date.now()}`,
      name: item._name || item.nameKey,
      description: item._description || item.descriptionKey,
      price: calculateTotalPrice(totalQuantity) / totalQuantity, // 단가로 저장
      originalPrice: item.originalPrice,
      imageUrl: item.imageUrl,
      category: item.category,
      restaurantId: item.restaurantId || '',
      restaurantName: '',
      customizations: customizations,
      selectedOptions: normalizedSelectedOptions,
      // #498: 입력형 옵션 사용자 입력 값 보존 (cart 편집 / sessionStorage / BE 전송)
      inputValues: inputValues,
      // #65: 옵션 편집용 원본 데이터 저장
      _originalOptions: apiOptions || [],
      // #280 보강: 장바구니 편집 모달에서 판매 시간 행 노출하려면 availability 보존 필요
      _availability: item.availability,
    };

    // selectedOptions를 SelectedOption[] 형식으로 변환
    const selectedOptionsList: SelectedOption[] = [];
    menuOptions.forEach(optionGroup => {
      const groupName = resolveCustomerDisplayName(
        optionGroup._name,
        t.optionGroups[optionGroup.nameKey as keyof typeof t.optionGroups],
        optionGroup.nameKey,
      ) ?? optionGroup.id;
      const isInputType =
        optionGroup.type === OptionType.NUMBER_INPUT
        || optionGroup.type === OptionType.TEXT_INPUT
        || optionGroup.type === OptionType.TEXTAREA;

      // #496 hotfix (사후 codex P1-2): 입력형 옵션의 사용자 입력값을
      // SelectedOption 으로 변환하여 BE 전송 (cart → checkout → order 전체 흐름).
      // 이전: inputValues 가 customizations 문자열만 첨부하고 BE 전송 데이터에서 누락.
      if (isInputType) {
        const value = (inputValues[optionGroup.id] || '').trim();
        if (value) {
          selectedOptionsList.push({
            id: optionGroup.id, // input type 은 group id 자체를 식별자로 사용
            groupId: optionGroup.id,
            _name: groupName,
            name: groupName,
            _groupName: groupName,
            groupName: groupName,
            additionalPrice: 0,
            input_value: value,
          });
        }
        return;
      }

      const selections = normalizedSelectedOptions[optionGroup.id] || [];
      selections.forEach(selectionId => {
        const option = optionGroup.options.find(opt => opt.id === selectionId);
        if (option) {
          const optionName = resolveCustomerDisplayName(
            option._name,
            t.options[option.nameKey as keyof typeof t.options],
            option.nameKey,
          ) ?? option.id;
          selectedOptionsList.push({
            id: selectionId,
            groupId: optionGroup.id,
            _name: optionName,
            name: optionName,
            _groupName: groupName,
            groupName: groupName,
            additionalPrice: option.additionalPrice,
          });
        }
      });
    });

    onAddToCart(cartItem, totalQuantity, selectedOptionsList);
    onClose();
  }, [item, t, selectedOptions, inputValues, calculateTotalPrice, totalQuantity, onAddToCart, onClose, menuOptions, apiOptions]);

  // 검증 및 스크롤 함수 — 첫 에러 메시지를 반환(null = 통과).
  // #630 codex P2-1: boolean 대신 메시지를 반환해 입력형 검증 실패(min/max/pattern) 시
  // 토스트가 일반 문구가 아닌 구체 사유를 표면화하도록 한다.
  const validateAndScroll = useCallback((): string | null => {
    let firstErrorGroupId: string | null = null;
    let firstErrorMessage: string | null = null;

    // 모든 옵션 그룹 검증
    for (const optionGroup of menuOptions) {
      const isInputTypeOption =
        optionGroup.type === OptionType.NUMBER_INPUT
        || optionGroup.type === OptionType.TEXT_INPUT
        || optionGroup.type === OptionType.TEXTAREA;

      // #493 R2 codex P1 시정: 입력형 옵션의 is_required 검증.
      // #499 R2 codex P1 시정: validation_rules + try/catch (invalid regex) +
      // NaN fail-closed + 전체 매칭 (^...$) + i18n 적용.
      // apiOptions 에서 원본 메타 조회 (transformApiToLocalOption 변환 시 손실).
      if (isInputTypeOption) {
        const apiGroup = apiOptions?.find(o => String(o.id) === optionGroup.id);
        const value = (inputValues[optionGroup.id] || '').trim();
        let inputError: string | null = null;

        if (apiGroup?.is_required && !value) {
          inputError = t.pleaseSelectRequired;
        } else if (value && apiGroup?.validation_rules) {
          const rules = apiGroup.validation_rules;
          if (optionGroup.type === OptionType.NUMBER_INPUT) {
            const num = Number(value);
            if (Number.isNaN(num)) {
              // R2 codex P1: NaN 입력 silent pass 차단 (fail-closed)
              inputError = t.validationNumberRequired || 'Enter a number';
            } else if (rules.min_value !== undefined && num < rules.min_value) {
              inputError = (t.validationMinValue || 'Min: {min}').replace('{min}', String(rules.min_value));
            } else if (rules.max_value !== undefined && num > rules.max_value) {
              inputError = (t.validationMaxValue || 'Max: {max}').replace('{max}', String(rules.max_value));
            }
          } else {
            if (rules.min_length !== undefined && value.length < rules.min_length) {
              inputError = (t.validationMinLength || 'Min: {min} chars').replace('{min}', String(rules.min_length));
            } else if (rules.max_length !== undefined && value.length > rules.max_length) {
              inputError = (t.validationMaxLength || 'Max: {max} chars').replace('{max}', String(rules.max_length));
            } else if (rules.pattern) {
              try {
                // R2 codex P1: 전체 매칭 (^...$) — HTML pattern 속성과 정합
                const fullMatch = new RegExp(`^(?:${rules.pattern})$`);
                if (!fullMatch.test(value)) {
                  inputError = t.validationPattern || 'Invalid format';
                }
              } catch {
                // R2 codex P1: invalid RegExp throw 차단 — fail-closed
                inputError = t.validationPattern || 'Invalid format';
              }
            }
          }
        }

        if (inputError) {
          if (!firstErrorGroupId) {
            firstErrorGroupId = optionGroup.id;
            firstErrorMessage = inputError;
          }
          setValidationErrors(prev => ({
            ...prev,
            [optionGroup.id]: inputError,
          }));
        }
        // selection 검증 스킵 — input type 은 selectedOptions 에 추적 안 됨.
        continue;
      }

      const selectedCount = (selectedOptions[optionGroup.id] || []).length;
      const minSelection = optionGroup.minSelection || 0;
      const maxSelection = optionGroup.maxSelection || 999;

      let hasError = false;

      if (selectedCount < minSelection) {
        hasError = true;
      } else if (selectedCount > maxSelection) {
        hasError = true;
      }

      if (hasError && !firstErrorGroupId) {
        firstErrorGroupId = optionGroup.id;
        firstErrorMessage = t.pleaseSelectRequired;
      }
    }

    // 에러가 있으면 검증 실패(false). 스크롤은 부수효과 — element 존재 여부와 무관하게
    // 검증 결과를 확정한다(#630 F-05: 이전엔 scroll element 미발견 시 return true 로 빠져
    // 검증을 통과시키는 fragile 경로였음 → 토스트/차단이 안 먹힘).
    if (firstErrorGroupId) {
      document.getElementById(`option-group-${firstErrorGroupId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      return firstErrorMessage ?? t.pleaseSelectRequired;
    }

    return null;
  }, [
    selectedOptions,
    menuOptions,
    inputValues,
    apiOptions,
    t.pleaseSelectRequired,
    t.validationMaxLength,
    t.validationMaxValue,
    t.validationMinLength,
    t.validationMinValue,
    t.validationNumberRequired,
    t.validationPattern,
    setValidationErrors,
  ]);

  if (!item || !isOpen) return null;

  return (
    <>
      {/* 백드롭 */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] transition-opacity duration-300"
        onClick={onClose}
      />

      {/* 모달 */}
      <div className="fixed inset-0 z-[70] flex items-end justify-center">
        <div className="w-full max-w-lg bg-background rounded-t-[24px] shadow-xl transform transition-transform duration-300 animate-slideUp h-full overflow-hidden flex flex-col">

          {/* 헤더 */}
          <div className="flex items-center justify-between border-b border-border bg-background px-[16px] py-[6px]">
            <button
              onClick={onClose}
              className="p-2 -ml-2 rounded-full hover:bg-muted transition-colors"
            >
              <svg className="w-[12px] h-[21px]" fill="none" preserveAspectRatio="none" viewBox="0 0 12 21">
                <path d="M11.588 0.41098C11.4583 0.280705 11.3042 0.177346 11.1345 0.106823C10.9649 0.0363008 10.783 0 10.5993 0C10.4156 0 10.2337 0.0363008 10.0641 0.106823C9.89442 0.177346 9.74031 0.280705 9.61059 0.41098L0.326717 9.71087C0.223149 9.8144 0.140982 9.93738 0.0849197 10.0728C0.0288573 10.2082 0 10.3533 0 10.4999C0 10.6464 0.0288573 10.7916 0.0849197 10.9269C0.140982 11.0623 0.223149 11.1853 0.326717 11.2888L9.61059 20.5887C10.158 21.1371 11.0406 21.1371 11.588 20.5887C12.1354 20.0404 12.1354 19.1562 11.588 18.6079L3.49955 10.4943L11.5992 2.38063C12.1354 1.84345 12.1354 0.948157 11.588 0.41098Z" fill="currentColor" className="text-foreground" />
              </svg>
            </button>
            <h1 className="text-[16px] font-medium text-foreground">
              {t.menuSelection}
            </h1>
            <div className="w-9" /> {/* 균형을 위한 공간 */}
          </div>

          {/* 스크롤 가능한 콘텐츠 */}
          <div className="flex-1 overflow-y-auto scrollbar-hide">
            {/* 메뉴 이미지 */}
            <div className="relative w-full h-[200px]">
              <ImageWithFallback
                src={item.imageUrl}
                alt={item.nameKey}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>

            {/* 메뉴 정보 */}
            <div className="px-[clamp(16px,4vw,24px)] py-[clamp(12px,3vw,16px)] min-[400px]:py-3">
              {/* 메뉴 정보 */}
              <div className="mb-[clamp(24px,6vw,32px)] text-center">
                <h2 className="text-[clamp(18px,4.5vw,24px)] font-bold text-foreground mb-[clamp(6px,1.5vw,8px)]">
                  {item._name || t.menu?.[item.nameKey as keyof typeof t.menu] || item.nameKey}
                </h2>
                <p className="text-[clamp(13px,3.2vw,16px)] text-muted-foreground leading-relaxed px-[clamp(8px,2vw,16px)]">
                  {item._description || t.menu?.[item.descriptionKey as keyof typeof t.menu] || ''}
                </p>
                {(() => {
                  // #280: 메뉴별 판매 시간대(time_slots)가 있으면 정보 행으로 노출.
                  //       빈 배열·전부 무효 슬롯이면 null이 반환되어 자동 스킵.
                  const slots = formatTimeSlots(item.availability?.time_slots);
                  if (!slots) return null;
                  return (
                    <p className="mt-[clamp(8px,2vw,12px)] text-[clamp(12px,3vw,14px)] text-muted-foreground">
                      <span className="font-medium">{getTimeSlotsLabel(locale)}: </span>
                      {slots}
                    </p>
                  );
                })()}
              </div>

              {/* 옵션 섹션 (옵션이 있을 때만 표시) */}
              {hasOptions && (
              <div className="space-y-[clamp(20px,5vw,32px)] min-[401px]:space-y-[26px]">
                {menuOptions.map((optionGroup) => (
                  <OptionGroupSection
                    key={optionGroup.id}
                    optionGroup={optionGroup}
                    selectedOptions={selectedOptions}
                    validationErrors={validationErrors}
                    inputValues={inputValues}
                    onInputChange={handleInputChange}
                    onOptionDecrement={handleOptionDecrement}
                    // #499: validation_rules 를 apiOptions 에서 조회하여 전달 (UI min/max + 검증)
                    validationRules={apiOptions?.find(o => String(o.id) === optionGroup.id)?.validation_rules}
                    translations={{
                      optionGroups: t.optionGroups || {},
                      optionGroupDescriptions: t.optionGroupDescriptions,
                      options: t.options || {},
                      optionTypes: t.optionTypes,
                      selectionStatus: t.selectionStatus,
                      // #394 Stage 4c follow-up — validation 메시지 i18n 키 propagation
                      // (이전: OptionGroupSection 의 hard-coded ko fallback 노출)
                      pleaseSelectRequired: t.pleaseSelectRequired,
                      pleaseSelectExactly: t.pleaseSelectExactly,
                      pleaseSelectMinimum: t.pleaseSelectMinimum,
                      pleaseSelectOption: t.pleaseSelectOption,
                      maximumSelection: t.maximumSelection,
                      // #492 (A1) R1 시정: 입력형 옵션 안내 i18n 전달.
                      inputTypePending: t.inputTypePending,
                    }}
                    formatPrice={formatPrice}
                    onOptionSelect={handleOptionSelect}
                  />
                ))}
              </div>
              )}
            </div>
          </div>

          {/* 하단 고정 버튼 영역 — #541: viewOnly=true 시 담기/수량 미렌더 */}
          {!viewOnly && (
            <div className="bg-background border-t border-border p-[clamp(16px,4vw,20px)]">
              {/* 메뉴 수량 조절 */}
              <div className="mb-[clamp(12px,3vw,16px)]">
                <MenuQuantityControl
                  quantity={totalQuantity}
                  minQuantity={1}
                  maxQuantity={10}
                  onQuantityChange={setTotalQuantity}
                  label={t.menuQuantity || '메뉴 수량'}
                />
              </div>

              {/* 장바구니 담기 버튼 — #493: fail-closed guard 해제 (입력형 옵션 fully orderable) */}
              <Button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const validationError = validateAndScroll();
                  if (!validationError) {
                    handleAddToCart();
                  } else {
                    // #630 F-05: 검증 실패 시 무피드백(스크롤만) → 명시적 토스트로 표면화.
                    // 에러 그룹이 이미 화면에 있으면 scrollIntoView 가 무동작이라 사용자가 변화를
                    // 못 느끼던 문제 해소. 필드 하이라이트(OptionGroupSection isUnderMin)와 병행.
                    // codex P2-1: 입력형 실패는 구체 사유(min/max/pattern) 그대로 노출.
                    // codex P2-2: 고정 id 로 연타 시 토스트 중첩 방지(sonner dedupe).
                    toast.error(validationError, { id: 'menu-option-validation' });
                  }
                }}
                className="w-full bg-secondary hover:bg-secondary/90 text-white text-[clamp(14px,3.5vw,16px)] font-semibold h-[50px] rounded-[5px] transition-colors flex items-center justify-center gap-2 shadow-lg relative z-[100]"
              >
                {t.addToCart} • {formatPrice(calculateTotalPrice(totalQuantity))}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
